import React from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import {useFormatMsg} from 'utils/useFormatMsg';
import {minmax} from 'utils/minmax';

/**
 * 기본 버튼 컴포넌트의 Props 인터페이스
 * @property {React.CSSProperties} [buttonStyle] - 버튼 스타일
 * @property {React.CSSProperties} [textStyle] - 텍스트 스타일
 * @property {boolean} [disabled] - 비활성화 여부
 * @property {() => void} [onClick] - 클릭 이벤트 핸들러
 * @property {1 | 0} [actived] - 활성화 상태
 * @property {string} text - 버튼 텍스트
 * @property {string} [imgSrc] - 이미지 소스
 * @property {React.CSSProperties} [imgStyle] - 이미지 스타일
 */
interface IDefaultButton {
  buttonStyle?: React.CSSProperties;
  textStyle?: React.CSSProperties;
  disabled?: boolean;
  onClick?: () => void;
  actived?: 1 | 0;
  text: string;
  imgSrc?: string;
  imgStyle?: React.CSSProperties;
}

/**
 * 기본 버튼 컴포넌트
 * 공통으로 사용되는 버튼 컴포넌트입니다.
 * @param {IDefaultButton} props - 버튼 컴포넌트의 props
 * @returns {JSX.Element} 기본 버튼 컴포넌트
 */
const DefaultButton = ({
  buttonStyle,
  textStyle,
  disabled,
  onClick,
  actived,
  text,
  imgSrc,
  imgStyle,
}: IDefaultButton) => {
  const txt = useFormatMsg(text) || text;

  return (
    <Wrapper
      style={buttonStyle}
      disabled={disabled ? 1 : 0}
      onClick={onClick}
      $actived={actived}>
      <TextStyle style={{...textStyle, whiteSpace: 'nowrap'}}>{txt}</TextStyle>
      {imgSrc && <Img src={imgSrc} style={imgStyle} />}
    </Wrapper>
  );
};
const TextStyle = styled.span`
  font-size: ${minmax(20)};
  font-weight: 400;
  letter-spacing: -0.96px;
  text-align: center;
  color: ${colors.gray};
  word-break: break-all;
  line-height: 100%;
  font-family: 'Noto Sans KR', sans-serif;
`;

const Img = styled.img`
  width: ${minmax(29)};
`;

const Wrapper = styled.button<{
  $actived: 1 | 0 | undefined;
  disabled: 1 | 0 | undefined;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${minmax(180)};
  height: ${minmax(60)};
  border-radius: ${minmax(10)};
  background-color: ${colors.borderGray};
  cursor: pointer;
  column-gap: 3px;
  ${(props: any) =>
    props.$actived &&
    css`
      background-color: ${colors.darkGray};
      & * {
        color: ${colors.white};
      }
    `}

  ${(props: any) =>
    props.disable &&
    css`
      visibility: hidden;
    `}
`;

export default DefaultButton;
